import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useDisplayTimeZone } from '../../hooks/useDisplayTimeZone';
import { COMMON_TIME_ZONES } from '../../lib/timezone';

/**
 * Company-level timezone override (when super admin delegated permission).
 */
export default function CompanyTimezoneSettings({ userData }) {
  const { workspace, companyId } = useWorkspace();
  const { timeZone: effectiveTz, timeZoneLabel, platformSettings, canManageTimezone } =
    useDisplayTimeZone(userData);

  const [timeZone, setTimeZone] = useState('');
  const [usePlatformDefault, setUsePlatformDefault] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const resolvedCompanyId = companyId || userData?.companyId;
  const platformTz = platformSettings?.displayTimeZone;

  useEffect(() => {
    const override = workspace?.displayTimeZone;
    if (override) {
      setTimeZone(override);
      setUsePlatformDefault(false);
    } else {
      setTimeZone(platformTz || effectiveTz);
      setUsePlatformDefault(true);
    }
  }, [workspace?.displayTimeZone, platformTz, effectiveTz]);

  if (!canManageTimezone.company) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Display times use the platform timezone ({timeZoneLabel}). Only roles delegated by
        your platform administrator can change the workspace timezone.
      </Alert>
    );
  }

  const handleSave = async () => {
    if (!resolvedCompanyId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const fn = httpsCallable(functions, 'updateCompanyDisplayTimeZone');
      await fn({
        companyId: resolvedCompanyId,
        displayTimeZone: usePlatformDefault ? null : timeZone,
      });
      setSuccess(
        usePlatformDefault
          ? 'Workspace now uses the platform default timezone.'
          : 'Workspace timezone updated.',
      );
    } catch (e) {
      console.error('CompanyTimezoneSettings:', e);
      setError(e.message || 'Could not update timezone.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Display timezone
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Currently showing times in <strong>{timeZoneLabel}</strong>. Override the platform
          default for this workspace only.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Stack spacing={2} sx={{ maxWidth: 420 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="company-tz-mode">Timezone source</InputLabel>
            <Select
              labelId="company-tz-mode"
              label="Timezone source"
              value={usePlatformDefault ? 'platform' : 'custom'}
              onChange={(e) => setUsePlatformDefault(e.target.value === 'platform')}
            >
              <MenuItem value="platform">Platform default</MenuItem>
              <MenuItem value="custom">Custom for this workspace</MenuItem>
            </Select>
          </FormControl>

          {!usePlatformDefault && (
            <FormControl fullWidth size="small">
              <InputLabel id="company-tz-label">Workspace timezone</InputLabel>
              <Select
                labelId="company-tz-label"
                label="Workspace timezone"
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
              >
                {COMMON_TIME_ZONES.map((z) => (
                  <MenuItem key={z.id} value={z.id}>
                    {z.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              Save timezone
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
